import React, { useEffect, useRef, useState, useCallback } from 'react';
import './App.css';
import {
  GeoPoint,
  calcDistance,
  calcTotalDistance,
  calcArea,
  calcAngle,
  formatDistance,
  formatArea,
  formatAngle,
  getAreaUnits,
  getDistanceUnits,
} from './geoUtils';

type MeasureMode = 'area' | 'distance' | 'angle';
type InputMode = 'manual' | 'auto';

/* ===== SVG Icon Components ===== */
const AreaIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="12,3 22,20 2,20" strokeLinejoin="round" />
    <circle cx="12" cy="3" r="2" fill="currentColor" stroke="none" />
    <circle cx="22" cy="20" r="2" fill="currentColor" stroke="none" />
    <circle cx="2" cy="20" r="2" fill="currentColor" stroke="none" />
  </svg>
);

const DistanceIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="4" y1="20" x2="20" y2="4" />
    <circle cx="4" cy="20" r="2.5" fill="currentColor" stroke="none" />
    <circle cx="20" cy="4" r="2.5" fill="currentColor" stroke="none" />
  </svg>
);

const AngleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="4,20 4,8 18,20" strokeLinejoin="round" />
    <path d="M4,16 Q8,16 8,20" strokeWidth="1.5" fill="none" />
    <circle cx="4" cy="8" r="2" fill="currentColor" stroke="none" />
    <circle cx="18" cy="20" r="2" fill="currentColor" stroke="none" />
  </svg>
);

const LocationIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="3" fill="currentColor" />
    <line x1="12" y1="2" x2="12" y2="5" />
    <line x1="12" y1="19" x2="12" y2="22" />
    <line x1="2" y1="12" x2="5" y2="12" />
    <line x1="19" y1="12" x2="22" y2="12" />
  </svg>
);

const MapTypeIcon = ({ isHybrid }: { isHybrid: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    {isHybrid ? (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3,14 L9,9 L14,13 L21,7" />
        <circle cx="16" cy="7" r="2" fill="currentColor" />
      </>
    ) : (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <line x1="3" y1="9" x2="21" y2="9" />
        <line x1="3" y1="15" x2="21" y2="15" />
        <line x1="9" y1="3" x2="9" y2="21" />
        <line x1="15" y1="3" x2="15" y2="21" />
      </>
    )}
  </svg>
);


const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const MinusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const UndoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4,10 L4,4 L10,4" />
    <path d="M4,4 L12,12" />
    <path d="M12,8 A8,8 0 1,1 4,16" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3,6 5,6 21,6" />
    <path d="M19,6 L18,20 C18,21 17,22 16,22 L8,22 C7,22 6,21 6,20 L5,6" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
    <path d="M9,6 V4 C9,3 10,2 11,2 L13,2 C14,2 15,3 15,4 V6" />
  </svg>
);

const WalkIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={style}>
    <path d="M13.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM9.8 8.9L7 23h2.1l1.8-8 2.1 2v6h2v-7.5l-2.1-2 .6-3C14.8 12 16.8 13 19 13v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1L6 8.3V13h2V9.6l1.8-.7"/>
  </svg>
);

const PointerIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
    <path d="M8 13V4.5a2.5 2.5 0 0 1 5 0V11" />
    <path d="M13 11v-1a2 2 0 0 1 4 0v1" />
    <path d="M17 12v-1a2 2 0 0 1 4 0v4.5c0 3.6-2.9 6.5-6.5 6.5H12c-3.2 0-6.1-2.4-6.5-5.6l-.7-5.4a2.2 2.2 0 0 1 4.1-1.3L10 13" />
  </svg>
);

const CurrentLocationPinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

function App() {
  /* ===== State ===== */
  const [measureMode, setMeasureMode] = useState<MeasureMode>('area');
  const [inputMode, setInputMode] = useState<InputMode>('manual');
  const [isHybrid, setIsHybrid] = useState(() => {
    return localStorage.getItem('mapType') !== 'roadmap';
  });
  const [points, setPoints] = useState<GeoPoint[]>([]);
  const [panelExpanded, setPanelExpanded] = useState(false);
  const [mapCenter, setMapCenter] = useState<GeoPoint>({ lat: 37.5665, lng: 126.978 });
  const [gpsTracking, setGpsTracking] = useState(false);
  const [myLocation, setMyLocation] = useState<GeoPoint | null>(null);
  const [heading, setHeading] = useState<number | null>(null);
  const [usePinImage, setUsePinImage] = useState(() => {
    const saved = localStorage.getItem('usePinImage');
    return saved !== null ? saved === 'true' : true;
  });

  useEffect(() => {
    localStorage.setItem('usePinImage', String(usePinImage));
  }, [usePinImage]);

  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const polylineRef = useRef<any>(null);
  const polygonRef = useRef<any>(null);
  const watchIdRef = useRef<number | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const labelsRef = useRef<any[]>([]);
  const myLocationOverlayRef = useRef<any>(null);
  
  const touchStartY = useRef(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartY.current) return;
    const diff = touchStartY.current - e.changedTouches[0].clientY;
    if (diff > 20) setPanelExpanded(true); // Swipe Up
    else if (diff < -20) setPanelExpanded(false); // Swipe Down
    touchStartY.current = 0;
  };

  const handleOrientation = useCallback((e: any) => {
    // webkitCompassHeading is iOS specific
    const h = e.webkitCompassHeading !== undefined ? e.webkitCompassHeading : e.alpha;
    if (h !== undefined && h !== null) {
      setHeading(h);
    }
  }, []);

  const requestCompassPermission = useCallback(async () => {
    if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
      try {
        const response = await (DeviceOrientationEvent as any).requestPermission();
        if (response === 'granted') {
          window.addEventListener('deviceorientation', handleOrientation);
          return true;
        }
      } catch (err) {
        console.error('Compass permission error:', err);
      }
    } else {
      window.addEventListener('deviceorientation', handleOrientation);
      return true;
    }
    return false;
  }, [handleOrientation]);

  const inputModeRef = useRef<InputMode>(inputMode);
  const measureModeRef = useRef<MeasureMode>(measureMode);
  const addPointRef = useRef<((pt?: GeoPoint) => void) | null>(null);

  useEffect(() => {
    inputModeRef.current = inputMode;
  }, [inputMode]);

  useEffect(() => {
    measureModeRef.current = measureMode;
  }, [measureMode]);

  /* ===== Initialize Map ===== */
  useEffect(() => {
    if (!mapContainerRef.current) return;

    const initMap = () => {
      if (!mapContainerRef.current || !window.kakao || !window.kakao.maps) return;

      const map = new window.kakao.maps.Map(mapContainerRef.current, {
        center: new window.kakao.maps.LatLng(mapCenter.lat, mapCenter.lng),
        level: 3,
        mapTypeId: isHybrid ? window.kakao.maps.MapTypeId.HYBRID : window.kakao.maps.MapTypeId.ROADMAP,
        disableDoubleClickZoom: true
      });

      mapRef.current = map;

      // Disable double click zoom
      const container = mapContainerRef.current;
      if (container) {
        container.addEventListener('dblclick', (e) => {
          e.stopPropagation();
        }, true);
      }

      // Update crosshair position on map move
      window.kakao.maps.event.addListener(map, 'center_changed', () => {
        const center = map.getCenter();
        setMapCenter({ lat: center.getLat(), lng: center.getLng() });
      });

      // Map click/touch handler: add new point
      window.kakao.maps.event.addListener(map, 'click', (mouseEvent: any) => {
        if (inputModeRef.current === 'manual') {
          const latlng = mouseEvent.latLng;
          setPoints((prev) => {
            const newPt = { lat: latlng.getLat(), lng: latlng.getLng() };
            if (measureModeRef.current === 'angle' && prev.length >= 3) return prev;
            return [...prev, newPt];
          });
        }
      });

      // Try geolocation on init
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const latlng = new window.kakao.maps.LatLng(pos.coords.latitude, pos.coords.longitude);
            map.setCenter(latlng);
            setMapCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
            setMyLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          },
          () => {},
          { enableHighAccuracy: true }
        );
      }
    };

    // Wait for Kakao SDK to load correctly
    const checkAndInit = () => {
      if (window.kakao && window.kakao.maps) {
        if (window.kakao.maps.load) {
          window.kakao.maps.load(() => initMap());
        } else {
          initMap();
        }
      } else {
        setTimeout(checkAndInit, 100);
      }
    };
    
    checkAndInit();

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===== Clear overlays ===== */
  const clearOverlays = useCallback(() => {
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    labelsRef.current.forEach((l) => l.setMap(null));
    labelsRef.current = [];
  }, []);

  /* ===== Draw overlays when points change or panel toggles ===== */
  useEffect(() => {
    if (!mapRef.current || !window.kakao || !window.kakao.maps) return;

    clearOverlays();
    const map = mapRef.current;
    const path = points.map((p) => new window.kakao.maps.LatLng(p.lat, p.lng));

    // Draw precise point dots and labels
    points.forEach((p, i) => {
      // 1. Precise Dot at the exact coordinate
      const dot = new window.kakao.maps.CustomOverlay({
        position: new window.kakao.maps.LatLng(p.lat, p.lng),
        content: usePinImage
          ? `<img src="/pin.webp" style="width: 32px; height: 32px; display: block; filter: drop-shadow(0px 2px 4px rgba(0,0,0,0.5));" />`
          : `<div style="
          width: 8px;
          height: 8px;
          background: white;
          border: 2px solid #333;
          border-radius: 50%;
          box-shadow: 0 0 4px rgba(0,0,0,0.5);
        "></div>`,
        map,
        xAnchor: 0.5,
        yAnchor: usePinImage ? 1 : 0.5,
        zIndex: 10,
      });
      markersRef.current.push(dot);

      // 2. Label above the point (Visible only when panel is expanded)
      const label = new window.kakao.maps.CustomOverlay({
        position: new window.kakao.maps.LatLng(p.lat, p.lng),
        content: `<div style="
          display: ${panelExpanded ? 'flex' : 'none'};
          background: linear-gradient(135deg, #e8a838, #d4881f);
          color: white;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 700;
          font-family: 'Noto Sans KR', sans-serif;
          border: 2px solid white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          transform: translateY(-5px);
          pointer-events: none;
        ">${i + 1}</div>`,
        map,
        xAnchor: 0.5,
        yAnchor: 1,
        zIndex: 11,
      });
      labelsRef.current.push(label);
    });

    // Draw polyline
    if (path.length >= 2) {
      const lineColor =
        measureMode === 'area'
          ? '#ff6b6b'
          : measureMode === 'distance'
          ? '#4ecdc4'
          : '#ffe66d';

      const polyline = new window.kakao.maps.Polyline({
        map,
        path: measureMode === 'area' && path.length >= 3 ? [...path, path[0]] : path,
        strokeWeight: 3,
        strokeColor: lineColor,
        strokeOpacity: 0.9,
        strokeStyle: 'solid',
      });
      polylineRef.current = polyline;
    }

    // Draw polygon fill for area mode
    if (measureMode === 'area' && path.length >= 3) {
      const polygon = new window.kakao.maps.Polygon({
        map,
        path,
        strokeWeight: 0,
        fillColor: '#ff6b6b',
        fillOpacity: 0.2,
      });
      polygonRef.current = polygon;
    }

    // Draw angle arc for angle mode
    if (measureMode === 'angle' && points.length === 3) {
      // Additional visual: angle arc rendered via custom overlay
      const arcOverlay = new window.kakao.maps.CustomOverlay({
        position: new window.kakao.maps.LatLng(points[1].lat, points[1].lng),
        content: `<div style="
          background: rgba(255,230,109,0.15);
          border: 2px solid #ffe66d;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          transform: translate(-50%, -50%);
          pointer-events: none;
        "></div>`,
        map,
        xAnchor: 0.5,
        yAnchor: 0.5,
        zIndex: 5,
      });
      labelsRef.current.push(arcOverlay);
    }

    // Draw segment distance labels
    if (measureMode === 'distance' && points.length >= 2) {
      for (let i = 0; i < points.length - 1; i++) {
        const midLat = (points[i].lat + points[i + 1].lat) / 2;
        const midLng = (points[i].lng + points[i + 1].lng) / 2;
        const dist = calcDistance(points[i], points[i + 1]);
        const segLabel = new window.kakao.maps.CustomOverlay({
          position: new window.kakao.maps.LatLng(midLat, midLng),
          content: `<div style="
            background: rgba(22,33,62,0.9);
            color: #4ecdc4;
            padding: 2px 8px;
            border-radius: 10px;
            font-size: 11px;
            font-weight: 600;
            font-family: 'Noto Sans KR', sans-serif;
            border: 1px solid rgba(78,205,196,0.3);
            white-space: nowrap;
            pointer-events: none;
          ">${formatDistance(dist)}</div>`,
          map,
          yAnchor: 1.5,
          zIndex: 9,
        });
        labelsRef.current.push(segLabel);
      }
    }
  }, [points, measureMode, inputMode, panelExpanded, clearOverlays, usePinImage]);

  /* ===== Draw My Location Overlay ===== */
  useEffect(() => {
    if (!mapRef.current || !window.kakao || !window.kakao.maps) return;
    if (myLocation) {
      const latlng = new window.kakao.maps.LatLng(myLocation.lat, myLocation.lng);
      if (!myLocationOverlayRef.current) {
        // Create as a DOM element to allow direct access for rotation
        const content = document.createElement('div');
        content.className = 'my-location-marker';
        const indicator = document.createElement('div');
        indicator.className = 'heading-indicator';
        indicator.style.display = 'none';
        content.appendChild(indicator);

        myLocationOverlayRef.current = new window.kakao.maps.CustomOverlay({
          position: latlng,
          map: mapRef.current,
          content: content,
          zIndex: 999,
        });
      } else {
        myLocationOverlayRef.current.setPosition(latlng);
        myLocationOverlayRef.current.setMap(mapRef.current);
      }
    } else {
      if (myLocationOverlayRef.current) {
        myLocationOverlayRef.current.setMap(null);
      }
    }
  }, [myLocation]);

  useEffect(() => {
    if (myLocationOverlayRef.current && heading !== null) {
      const node = myLocationOverlayRef.current.getContent() as HTMLElement;
      if (node) {
        const indicator = node.querySelector('.heading-indicator') as HTMLElement;
        if (indicator) {
          indicator.style.display = 'block';
          // Rotate based on heading. Use transform.
          indicator.style.transform = `translate(-50%, -100%) rotate(${heading}deg)`;
        }
      }
    }
  }, [heading]);

  /* ===== Add point ===== */
  const addPoint = useCallback(
    (pt?: GeoPoint) => {
      let targetPt = pt;
      
      // If no point provided (manual plus button), get current visual center directly from map for precision
      if (!targetPt && mapRef.current) {
        const center = mapRef.current.getCenter();
        targetPt = { lat: center.getLat(), lng: center.getLng() };
      }
      
      if (!targetPt) return;

      setPoints((prev) => {
        // angle mode: max 3 points
        if (measureMode === 'angle' && prev.length >= 3) return prev;
        return [...prev, targetPt!];
      });
    },
    [measureMode]
  );

  useEffect(() => {
    addPointRef.current = addPoint;
  }, [addPoint]);

  /* ===== Remove last point ===== */
  const removeLastPoint = useCallback(() => {
    setPoints((prev) => prev.slice(0, -1));
  }, []);

  /* ===== Clear all points ===== */
  const clearAllPoints = useCallback(() => {
    setPoints([]);
  }, []);

  /* ===== Toggle map type ===== */
  const toggleMapType = useCallback(() => {
    if (!mapRef.current || !window.kakao || !window.kakao.maps) return;
    const map = mapRef.current;
    if (isHybrid) {
      map.setMapTypeId(window.kakao.maps.MapTypeId.ROADMAP);
      localStorage.setItem('mapType', 'roadmap');
    } else {
      map.setMapTypeId(window.kakao.maps.MapTypeId.HYBRID);
      localStorage.setItem('mapType', 'hybrid');
    }
    setIsHybrid(!isHybrid);
  }, [isHybrid]);

  /* ===== Toggle input mode ===== */
  const toggleInputMode = useCallback(() => {
    if (inputMode === 'manual') {
      setInputMode('auto');
    } else {
      setInputMode('manual');
      // Stop GPS tracking
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      setGpsTracking(false);
    }
  }, [inputMode]);

  /* ===== GPS auto tracking ===== */
  const toggleGpsTracking = useCallback(() => {
    if (gpsTracking) {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      setGpsTracking(false);
      return;
    }

    if (!navigator.geolocation) {
      alert('GPS를 지원하지 않는 브라우저입니다.');
      return;
    }

    // Request compass permission on manual touch
    requestCompassPermission();

    setGpsTracking(true);
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const pt: GeoPoint = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        setMyLocation(pt);

        if (inputMode === 'auto') {
          // Auto mode: add points automatically
          setPoints((prev) => {
            if (prev.length === 0) return [pt];
            const last = prev[prev.length - 1];
            const dist = calcDistance(last, pt);
            if (dist > 5) {
              // Add point only if moved > 5 meters
              return [...prev, pt];
            }
            return prev;
          });
        }

        // Pan map to current location
        if (mapRef.current && window.kakao && window.kakao.maps) {
          mapRef.current.panTo(new window.kakao.maps.LatLng(pt.lat, pt.lng));
        }
      },
      (err) => {
        console.error('GPS error:', err);
        setGpsTracking(false);
      },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 5000 }
    );
  }, [gpsTracking, inputMode]);

  /* ===== Go to my location ===== */
  const goToMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert('GPS를 지원하지 않는 브라우저입니다.');
      return;
    }

    // Request compass permission
    requestCompassPermission();

    if (!window.kakao || !window.kakao.maps) {
      alert('지도를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latlng = new window.kakao.maps.LatLng(pos.coords.latitude, pos.coords.longitude);
        mapRef.current?.panTo(latlng);
        setMyLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => alert('위치 정보를 가져올 수 없습니다.'),
      { enableHighAccuracy: true }
    );
  }, [requestCompassPermission]);

  /* ===== Add Current Location Point ===== */
  const addCurrentLocationPoint = useCallback(() => {
    if (!navigator.geolocation) {
      alert('GPS를 지원하지 않는 브라우저입니다.');
      return;
    }

    requestCompassPermission();

    if (!window.kakao || !window.kakao.maps) {
      alert('지도를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const pt = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        mapRef.current?.panTo(new window.kakao.maps.LatLng(pt.lat, pt.lng));
        setMyLocation(pt);
        addPointRef.current?.(pt);
      },
      () => alert('위치 정보를 가져올 수 없습니다.'),
      { enableHighAccuracy: true }
    );
  }, [requestCompassPermission]);

  /* ===== Mode change: clear points ===== */
  const handleModeChange = useCallback(
    (mode: MeasureMode) => {
      if (mode === measureMode) return;
      setMeasureMode(mode);
      clearAllPoints();
    },
    [measureMode, clearAllPoints]
  );

  /* ===== Computed results ===== */
  const totalDist = calcTotalDistance(points);
  const area = measureMode === 'area' && points.length >= 3 ? calcArea(points) : 0;
  const angle =
    measureMode === 'angle' && points.length === 3
      ? calcAngle(points[0], points[1], points[2])
      : 0;

  const areaUnits = getAreaUnits(area);
  const distUnits = getDistanceUnits(totalDist);

  /* ===== Render ===== */
  return (
    <div className="app-container">
      {/* Header */}
      <header className="app-header">
        <div className="app-title">
          <div className="logo-icon">📐</div>
          <h1>GPS 면적측정기</h1>
        </div>
        <div className="header-actions">
          <button
            className={`control-btn ${inputMode === 'auto' ? 'active' : ''}`}
            onClick={toggleInputMode}
            id="mode-toggle-btn"
            style={{ width: 64, height: 40, borderRadius: 20, padding: 0 }}
            title={inputMode === 'auto' ? '자동 (GPS)' : '수동 (터치)'}
          >
            {inputMode === 'auto' ? <WalkIcon style={{ width: 24, height: 24 }} /> : <PointerIcon style={{ width: 24, height: 24 }} />}
          </button>
        </div>
      </header>

      {/* Mode Tabs */}
      <div className="mode-tabs">
        <button
          className={`mode-tab area-tab ${measureMode === 'area' ? 'active' : ''}`}
          onClick={() => handleModeChange('area')}
          id="area-tab-btn"
        >
          <span className="tab-icon"><AreaIcon /></span>
          면적
        </button>
        <button
          className={`mode-tab distance-tab ${measureMode === 'distance' ? 'active' : ''}`}
          onClick={() => handleModeChange('distance')}
          id="distance-tab-btn"
        >
          <span className="tab-icon"><DistanceIcon /></span>
          거리
        </button>
        <button
          className={`mode-tab angle-tab ${measureMode === 'angle' ? 'active' : ''}`}
          onClick={() => handleModeChange('angle')}
          id="angle-tab-btn"
        >
          <span className="tab-icon"><AngleIcon /></span>
          각도
        </button>
      </div>

      {/* Map */}
      <div className="map-container">
        <div id="kakao-map" ref={mapContainerRef} />

        {/* Center crosshair for manual mode */}
        <div className={`center-crosshair ${inputMode === 'manual' ? 'visible' : ''}`}>
          <div className="dot" />
        </div>

        {/* Right side controls */}
        <div className="map-controls">
          <button
            className={`control-btn btn-tooltip ${gpsTracking ? 'active' : ''}`}
            onClick={inputMode === 'auto' ? toggleGpsTracking : goToMyLocation}
            data-tooltip={inputMode === 'auto' ? (gpsTracking ? 'GPS 추적 중지' : 'GPS 추적 시작') : '내 위치'}
            id="location-btn"
          >
            <LocationIcon />
          </button>
          <button
            className={`control-btn btn-tooltip ${isHybrid ? 'active' : ''}`}
            onClick={toggleMapType}
            data-tooltip={isHybrid ? '일반지도' : '위성지도'}
            id="maptype-btn"
          >
            <MapTypeIcon isHybrid={isHybrid} />
          </button>
        </div>



        {/* Coordinate display */}
        <div className="coord-info">
          {mapCenter.lat.toFixed(6)}, {mapCenter.lng.toFixed(6)}
        </div>

        {/* Bottom Panel */}
        <div className={`bottom-panel ${!panelExpanded ? 'collapsed' : 'expanded'}`}>
          {/* Floating Controls Attached to Panel */}
          <div className="map-controls-action">
            <button
              className="control-btn btn-tooltip"
              onClick={addCurrentLocationPoint}
              data-tooltip="현재 위치에 포인트 추가"
              id="add-current-location-point-btn"
              style={{ marginBottom: '16px' }}
            >
              <CurrentLocationPinIcon />
            </button>
            <button
              className="control-btn btn-tooltip"
              onClick={() => addPoint()}
              data-tooltip="포인트 추가"
              id="add-point-btn"
              disabled={inputMode === 'auto'}
              style={inputMode === 'auto' ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            >
              <PlusIcon />
            </button>
            <button
              className="control-btn btn-tooltip"
              onClick={removeLastPoint}
              data-tooltip="실행취소 (마지막 수정 삭제)"
              id="remove-point-btn"
              style={points.length === 0 ? { opacity: 0.4, pointerEvents: 'none' } : {}}
            >
              <UndoIcon />
            </button>
            <button
              className="control-btn btn-tooltip danger"
              onClick={clearAllPoints}
              data-tooltip="모두 초기화"
              id="clear-point-btn"
              style={points.length === 0 ? { opacity: 0.4, pointerEvents: 'none' } : {}}
            >
              <TrashIcon />
            </button>
          </div>

          <div 
            className="panel-handle" 
            onClick={() => setPanelExpanded(!panelExpanded)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="handle-bar" />
          </div>
          <div className="panel-content">
            {/* Area Result */}
            {measureMode === 'area' && (
              <div className="fade-in">
                <div className="result-section">
                  <div className="result-label">면적 측정 결과</div>
                  <div className="result-value area-color">
                    {points.length < 3 ? (
                      <span style={{ fontSize: 16, color: 'var(--text-muted)' }}>
                        3개 이상의 포인트를 추가하세요
                      </span>
                    ) : (
                      <>
                        {formatArea(area)}
                        <span className="result-unit">
                          ({areaUnits.pyeong.toFixed(1)} 평)
                        </span>
                      </>
                    )}
                  </div>
                  {points.length >= 3 && (
                    <div className="result-sub">
                      둘레: {formatDistance(totalDist + calcDistance(points[points.length - 1], points[0]))}
                    </div>
                  )}
                </div>
                {points.length >= 3 && (
                  <div className="unit-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    <div className="unit-card">
                      <div className="unit-name">ha</div>
                      <div className="unit-val">{areaUnits.hectare.toFixed(4)}</div>
                    </div>
                    <div className="unit-card">
                      <div className="unit-name">km²</div>
                      <div className="unit-val">{areaUnits.sqkm.toFixed(6)}</div>
                    </div>
                    <div className="unit-card">
                      <div className="unit-name">acre</div>
                      <div className="unit-val">{areaUnits.acre.toFixed(4)}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Distance Result */}
            {measureMode === 'distance' && (
              <div className="fade-in">
                <div className="result-section">
                  <div className="result-label">거리 측정 결과</div>
                  <div className="result-value distance-color">
                    {points.length < 2 ? (
                      <span style={{ fontSize: 16, color: 'var(--text-muted)' }}>
                        2개 이상의 포인트를 추가하세요
                      </span>
                    ) : (
                      formatDistance(totalDist)
                    )}
                  </div>
                  {points.length >= 2 && (
                    <div className="result-sub">구간 수: {points.length - 1}</div>
                  )}
                </div>
                {points.length >= 2 && (
                  <div className="unit-grid">
                    <div className="unit-card">
                      <div className="unit-name">m</div>
                      <div className="unit-val">{distUnits.m.toFixed(1)}</div>
                    </div>
                    <div className="unit-card">
                      <div className="unit-name">km</div>
                      <div className="unit-val">{distUnits.km.toFixed(3)}</div>
                    </div>
                    <div className="unit-card">
                      <div className="unit-name">ft</div>
                      <div className="unit-val">{distUnits.ft.toFixed(1)}</div>
                    </div>
                    <div className="unit-card">
                      <div className="unit-name">mi</div>
                      <div className="unit-val">{distUnits.mi.toFixed(4)}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Angle Result */}
            {measureMode === 'angle' && (
              <div className="fade-in">
                <div className="result-section">
                  <div className="result-label">각도 측정 결과</div>
                  <div className="result-value angle-color">
                    {points.length < 3 ? (
                      <span style={{ fontSize: 16, color: 'var(--text-muted)' }}>
                        3개의 포인트를 추가하세요 (꼭짓점이 2번째)
                      </span>
                    ) : (
                      <>
                        {angle.toFixed(2)}°
                        <span className="result-unit">{formatAngle(angle)}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Point List */}
            {points.length > 0 && (
              <div className="point-list fade-in">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h3 style={{ margin: 0 }}>측정 포인트 ({points.length})</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <img src="/pin.webp" alt="pin" style={{ width: '18px', height: '18px', objectFit: 'contain', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }} />
                      <span style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 500 }}>핀 마커</span>
                    </div>
                    <div 
                      className={`custom-toggle ${usePinImage ? 'active' : ''}`}
                      onClick={() => setUsePinImage(!usePinImage)}
                    >
                      <div className="toggle-knob"></div>
                    </div>
                  </div>
                </div>
                {points.map((p, i) => (
                  <div className="point-item" key={i}>
                    <span className="point-idx">{i + 1}</span>
                    <span className="point-coords">
                      {p.lat.toFixed(6)}, {p.lng.toFixed(6)}
                    </span>
                    <button
                      className="point-remove"
                      onClick={() => setPoints((prev) => prev.filter((_, j) => j !== i))}
                      title="삭제"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}


          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
